import { useState } from 'react'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { StatusBadge } from '@/components/common/StatusBadge'
import { useCompleteElementStage, useElementGate } from '../hooks'
import type { ContractElement } from '../types'
import { OverrideElementStageDialog } from './OverrideElementStageDialog'
import { ElementFieldsPanel } from './ElementFieldsPanel'

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zmiany etapu'
}

export function ElementCard({ contractId, element }: { contractId: number; element: ContractElement }) {
  const complete = useCompleteElementStage(contractId)
  const gateQ = useElementGate(contractId, element.id)
  const [overriding, setOverriding] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const hasStage = element.stageLabel != null

  const gateClosed = gateQ.data ? !gateQ.data.canComplete : false
  const missingFieldCodes = gateQ.data?.missingFields.map(f => f.fieldCode) ?? []
  const gateTitle = gateClosed
    ? `Niespełnione wymagania: ${missingFieldCodes.join(', ') || 'uzupełnij pola elementu'}`
    : undefined

  return (
    <div className="border rounded-lg bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="size-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="size-4 text-slate-400 shrink-0" />
          )}
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-slate-800 truncate">{element.productLabel}</span>
            <span className="font-mono text-[11px] text-slate-400">{element.productCode}</span>
          </span>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          {hasStage ? (
            <StatusBadge label={element.stageLabel!} variant={element.stageColor ?? 'neutral'} />
          ) : (
            <span className="text-xs text-slate-400 italic">brak etapów</span>
          )}

          {hasStage && gateClosed && (
            <span
              title={gateTitle}
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] px-2 py-0.5"
            >
              ⚠ wymagania
            </span>
          )}

          {hasStage && (
            <>
              {element.nextStage && (
                <button
                  type="button"
                  disabled={complete.isPending || gateClosed}
                  title={gateTitle}
                  onClick={() =>
                    complete.mutate(element.id, { onError: e => toast.error(formatError(e)) })
                  }
                  className="rounded bg-sky-600 text-white px-2 py-1 text-xs disabled:opacity-50"
                >
                  Zakończ etap
                </button>
              )}
              <button
                type="button"
                onClick={() => setOverriding(true)}
                className="rounded border px-2 py-1 text-xs text-slate-600 hover:text-sky-700"
              >
                Zmień
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-slate-50 px-4 py-3">
          <ElementFieldsPanel contractId={contractId} element={element} />
        </div>
      )}

      {overriding && (
        <OverrideElementStageDialog
          contractId={contractId}
          element={element}
          onClose={() => setOverriding(false)}
        />
      )}
    </div>
  )
}
